<template>
    <div class="practice-container">
        <!-- 添加TopBar组件 -->
        <TopBar :user="user" :apps="apps" />

        <!-- 页面头部 -->
        <div class="practice-header">
            <h1 class="practice-title">
                <i class="icon">📝</i>
                {{ bookType === 'abhsChinese' ? '中文' : '英文' }}默写练习
            </h1>
        </div>

        <!-- 主要内容区域 -->
        <div class="practice-main" v-if="!loading">
            <!-- 练习控制面板 -->
            <div class="control-panel">
                <div class="book-info">
                    <h3>{{ bookName }}</h3>
                    <p>{{ bookType === 'abhsChinese' ? '中文字词' : '英语单词' }}</p>
                </div>
                <div class="controls">
                    <button @click="pausePractice" class="control-btn" v-if="!isPaused">
                        暂停
                    </button>
                    <button @click="resumePractice" class="control-btn" v-else>
                        继续
                    </button>
                    <button @click="exitPractice" class="control-btn exit-btn">
                        退出
                    </button>
                </div>
            </div>

            <!-- 练习统计 -->
            <div class="practice-stats">
                <div class="stat-item">
                    <span class="label">进度</span>
                    <span class="value">{{ currentWordIndex + 1 }}/{{ words.length }}</span>
                </div>
                <div class="stat-item">
                    <span class="label">正确</span>
                    <span class="value correct">{{ correctCount }}</span>
                </div>
                <div class="stat-item">
                    <span class="label">错误</span>
                    <span class="value wrong">{{ wrongCount }}</span>
                </div>
                <div class="stat-item">
                    <span class="label">正确率</span>
                    <span class="value">{{ accuracy }}%</span>
                </div>
                <div class="stat-item">
                    <span class="label">用时</span>
                    <span class="value">{{ formatTime(elapsedTime) }}</span>
                </div>
            </div>

            <!-- 练习区域 -->
            <div class="practice-area" v-if="currentWord">
                <div class="word-display">
                    <div class="word">{{ currentWord.name }}</div>
                    <div class="description">{{ currentWord.desc }}</div>
                </div>

                <div class="user-input-area">
                    <input v-model="userAnswer" @keyup.enter="checkAnswer" :disabled="isPaused" placeholder="请输入答案..."
                        class="answer-input" ref="answerInput" />
                    <button @click="checkAnswer" :disabled="isPaused" class="check-btn">
                        检查
                    </button>
                </div>

                <div class="feedback" v-if="showFeedback">
                    <div :class="['feedback-message', feedbackType]">
                        {{ feedbackMessage }}
                    </div>
                </div>
            </div>

            <!-- 结果统计 -->
            <div class="results" v-if="showResults">
                <h3>练习结果</h3>
                <div class="result-stats">
                    <div class="stat-item">
                        <span class="label">总题数</span>
                        <span class="value">{{ words.length }}</span>
                    </div>
                    <div class="stat-item">
                        <span class="label">正确</span>
                        <span class="value correct">{{ correctCount }}</span>
                    </div>
                    <div class="stat-item">
                        <span class="label">错误</span>
                        <span class="value wrong">{{ wrongCount }}</span>
                    </div>
                    <div class="stat-item">
                        <span class="label">正确率</span>
                        <span class="value">{{ accuracy }}%</span>
                    </div>
                    <div class="stat-item">
                        <span class="label">用时</span>
                        <span class="value">{{ formatTime(elapsedTime) }}</span>
                    </div>
                </div>

                <div class="wrong-words" v-if="wrongWords.length > 0">
                    <h4>错误单词</h4>
                    <div class="wrong-words-list">
                        <div v-for="(word, index) in wrongWords" :key="index" class="wrong-word-item">
                            <span class="word">{{ word.name }}</span>
                            <span class="desc">{{ word.desc }}</span>
                            <span class="user-answer">你的答案: {{ word.userAnswer }}</span>
                        </div>
                    </div>
                </div>

                <div class="result-actions">
                    <button @click="restartPractice" class="action-btn">重新开始</button>
                    <button @click="goToGallery" class="action-btn">返回词库</button>
                </div>
            </div>
        </div>

        <!-- 加载状态 -->
        <div v-if="loading" class="loading-overlay">
            <div class="loading-spinner">
                <div class="spinner"></div>
                <p>{{ loadingMessage }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TopBar from '@/components/TopBar.vue'
import AuthService from '@/services/authService.js'
// import UserService from '@/services/userService.js'
import AbhsService from '@/services/abhsService.js'
import UserMainService from '@/services/userMainService.js'
import config from '@/config'

export default {
    name: 'PracticeView',
    components: {
        TopBar
    },
    setup() {
        const route = useRoute()
        const router = useRouter()

        // 响应式数据
        const user = ref({
            name: '用户名',
            id: 1,
            userType: 1,
            avatar: require('@/assets/logo.png')
        })

        const apps = ref([])
        const loading = ref(true)
        const loadingMessage = ref('加载中...')

        // 练习相关数据
        const bookName = ref('')
        const bookType = ref('abhsWords')
        const words = ref([])
        const currentWordIndex = ref(0)
        const currentWord = computed(() => words.value[currentWordIndex.value])
        const userAnswer = ref('')

        // 练习状态
        const isPaused = ref(false)
        const showResults = ref(false)
        const showFeedback = ref(false)
        const feedbackType = ref('')
        const feedbackMessage = ref('')

        // 统计数据
        const correctCount = ref(0)
        const wrongCount = ref(0)
        const wrongWords = ref([])
        const startTime = ref(null)
        const elapsedTime = ref(0)
        const timer = ref(null)

        // 计算属性
        const accuracy = computed(() => {
            const total = correctCount.value + wrongCount.value
            return total > 0 ? Math.round((correctCount.value / total) * 100) : 0
        })

        // 加载用户信息
        const loadUserInfo = () => {
            const currentUser = AuthService.getCurrentUser()
            if (currentUser) {
                console.log('当前用户信息:', currentUser);
                user.value.name = currentUser.nickname || currentUser.username || '用户名';
                user.value.id = currentUser.userid || currentUser.id || 1;
                user.value.userType = currentUser.userType || 1;
                console.log("icon:", currentUser.icon);
                // 如果有头像信息，则更新头像路径
                if (currentUser.icon) {
                    // 规范化路径分隔符，将反斜杠转换为正斜杠
                    const normalizedIcon = currentUser.icon.replace(/\\/g, '/');
                    console.log('规范化后的icon路径:', normalizedIcon);

                    // 构建完整的后端服务器地址，确保头像从8000端口获取
                    if (normalizedIcon.startsWith('http')) {
                        console.log('完整URL:', normalizedIcon);
                        // 如果已经是完整URL，则直接使用
                        user.value.avatar = normalizedIcon;
                    } else if (normalizedIcon.startsWith('/extra-files/uploads/')) {
                        console.log('extra-files URL:', normalizedIcon);
                        // 从后端服务器的8000端口获取头像
                        user.value.avatar = `${config.API_BASE_URL}${normalizedIcon}`;
                    } else if (normalizedIcon.startsWith('uploads/')) {
                        console.log('uploads URL:', normalizedIcon);
                        // 从后端服务器的8000端口获取头像
                        //user.value.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;

                        user.value.avatar = `${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
                        //查看地址：`${config.API_BASE_URL}/extra-files/${normalizedIcon}`;
                        console.log('user.value.avatar', user.value.avatar);
                    } else {
                        console.log('img URL:', normalizedIcon);
                        user.value.avatar = `${config.API_BASE_URL}/extra-files/uploads/${normalizedIcon}`;
                    }
                } else {
                    console.log('使用默认头像');
                    // 使用默认头像
                    user.value.avatar = require('@/assets/logo.png');
                }
            }
        }

        // 加载应用信息
        const loadAppInfo = async () => {
            try {
                const response = await UserMainService.getAllApps(user.value.userType)
                if (response.status === 200 && response.apps) {
                    apps.value = response.apps.map(app => ({
                        id: app.id,
                        name: app.name,
                        desc: app.desc,
                        link: app.name === '快速抢答' ? '/abhs' : app.link,
                        icon: app.icon || 'code',
                        used: app.used || false
                    }))
                }
            } catch (error) {
                console.error('获取应用数据失败:', error)
                apps.value = [
                    { id: 1, name: 'OJ练习', desc: '在线编程练习', link: '/CppOJ/', icon: 'code', used: true },
                    { id: 2, name: '任务清单', desc: '待办事项管理', link: '/todo', icon: 'list', used: true },
                    { id: 3, name: '时间线', desc: '时间记录管理', link: '/TimeLine/', icon: 'timeline', used: false },
                    { id: 4, name: '知识图谱', desc: '知识关系展示', link: '/Graph/', icon: 'share', used: false },
                    { id: 5, name: '快速抢答', desc: '抢答与记忆训练', link: '/abhs', icon: 'quiz', used: false },
                    { id: 6, name: '文件管理', desc: '文件上传下载', link: '/FileManage/', icon: 'folder', used: false },
                    { id: 7, name: '数据可视化', desc: '数据图表展示', link: '/DataVisual/', icon: 'bar_chart', used: false }
                ]
            }
        }

        // 加载单词数据
        const loadWords = async () => {
            try {
                loading.value = true
                loadingMessage.value = '加载单词...'

                // 从路由参数获取书籍名称
                bookName.value = route.query.bookName || ''
                bookType.value = route.query.bookType || 'abhsWords'

                if (!bookName.value) {
                    alert('未指定单词表')
                    router.push('/abhs')
                    return
                }

                const response = await AbhsService.getWords(
                    user.value.id,
                    bookName.value
                )

                if (response.status === 200) {
                    words.value = response.message || []
                    if (words.value.length > 0) {
                        currentWordIndex.value = 0
                        userAnswer.value = ''
                        startTimer()
                    } else {
                        alert('当前单词表为空')
                        router.push('/abhs')
                    }
                }
            } catch (error) {
                console.error('加载单词失败:', error)
                alert('加载单词失败')
                router.push('/abhs')
            } finally {
                loading.value = false
            }
        }

        // 开始计时器
        const startTimer = () => {
            startTime.value = Date.now()
            timer.value = setInterval(() => {
                if (!isPaused.value) {
                    elapsedTime.value = Math.floor((Date.now() - startTime.value) / 1000)
                }
            }, 1000)
        }

        // 暂停练习
        const pausePractice = () => {
            isPaused.value = true
        }

        // 继续练习
        const resumePractice = () => {
            isPaused.value = false
        }

        // 退出练习
        const exitPractice = () => {
            if (confirm('确定要退出练习吗？')) {
                router.push('/abhs')
            }
        }

        // 检查答案
        const checkAnswer = () => {
            if (!currentWord.value || !userAnswer.value.trim() || isPaused.value) return

            const isCorrect = userAnswer.value.trim().toLowerCase() === currentWord.value.name.toLowerCase()

            // 显示反馈
            showFeedback.value = true
            if (isCorrect) {
                feedbackType.value = 'correct'
                feedbackMessage.value = '正确！'
                correctCount.value++
            } else {
                feedbackType.value = 'wrong'
                feedbackMessage.value = '错误！'
                wrongCount.value++
                wrongWords.value.push({
                    name: currentWord.value.name,
                    desc: currentWord.value.desc,
                    userAnswer: userAnswer.value
                })
            }

            // 更新单词掌握程度
            AbhsService.changeStrength(
                user.value.id,
                currentWord.value.fromNode_id,
                currentWord.value.toNode_id,
                isCorrect
            ).catch(error => {
                console.error('更新单词掌握程度失败:', error)
            })

            // 清除反馈
            setTimeout(() => {
                showFeedback.value = false

                // 移动到下一个单词
                if (currentWordIndex.value < words.value.length - 1) {
                    currentWordIndex.value++
                    userAnswer.value = ''
                    nextTick(() => {
                        // 注意：在Composition API中，需要通过模板引用访问DOM元素
                    })
                } else {
                    // 练习结束
                    finishPractice()
                }
            }, 1000)
        }

        // 完成练习
        const finishPractice = () => {
            clearInterval(timer.value)
            showResults.value = true
        }

        // 重新开始练习
        const restartPractice = () => {
            // 重置状态
            currentWordIndex.value = 0
            correctCount.value = 0
            wrongCount.value = 0
            wrongWords.value = []
            userAnswer.value = ''
            showResults.value = false
            elapsedTime.value = 0

            // 重新开始计时
            clearInterval(timer.value)
            startTimer()
        }

        // 返回词库
        const goToGallery = () => {
            router.push('/abhs')
        }

        // 格式化时间
        const formatTime = (seconds) => {
            const mins = Math.floor(seconds / 60)
            const secs = seconds % 60
            return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
        }

        // 初始化应用
        const initApp = async () => {
            try {
                console.log('Practice系统初始化中...')

                // 加载用户信息和应用信息
                loadUserInfo()
                await loadAppInfo()

                // 加载单词数据
                await loadWords()

                console.log('Practice系统初始化完成')
            } catch (error) {
                console.error('Practice系统初始化失败:', error)
            } finally {
                loading.value = false
            }
        }

        // 组件挂载时初始化
        onMounted(() => {
            initApp()
        })

        // 组件卸载时清理
        onUnmounted(() => {
            if (timer.value) {
                clearInterval(timer.value)
            }
        })

        return {
            user,
            apps,
            loading,
            loadingMessage,
            bookName,
            bookType,
            words,
            currentWordIndex,
            currentWord,
            userAnswer,
            isPaused,
            showResults,
            showFeedback,
            feedbackType,
            feedbackMessage,
            correctCount,
            wrongCount,
            wrongWords,
            elapsedTime,
            accuracy,
            pausePractice,
            resumePractice,
            exitPractice,
            checkAnswer,
            restartPractice,
            goToGallery,
            formatTime
        }
    }
}
</script>

<style scoped>
.practice-container {
    min-height: 100vh;
    background-color: #f5f5f5;
}

.practice-header {
    background-color: #fff;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.practice-title {
    margin: 0;
    font-size: 24px;
    color: #333;
}

.practice-main {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
}

.control-panel {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #fff;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.book-info h3 {
    margin: 0 0 5px 0;
    color: #333;
}

.book-info p {
    margin: 0;
    color: #666;
}

.controls {
    display: flex;
    gap: 10px;
}

.control-btn {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s ease;
}

.control-btn:not(.exit-btn) {
    background-color: #6c757d;
    color: white;
}

.control-btn:not(.exit-btn):hover {
    background-color: #5a6268;
}

.exit-btn {
    background-color: #dc3545;
    color: white;
}

.exit-btn:hover {
    background-color: #c82333;
}

.practice-stats {
    display: flex;
    justify-content: space-around;
    background-color: #fff;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-item {
    text-align: center;
}

.stat-item .label {
    display: block;
    font-size: 14px;
    color: #666;
    margin-bottom: 5px;
}

.stat-item .value {
    display: block;
    font-size: 20px;
    font-weight: bold;
    color: #333;
}

.correct {
    color: #28a745;
}

.wrong {
    color: #dc3545;
}

.practice-area {
    background-color: #fff;
    border-radius: 8px;
    padding: 30px;
    text-align: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.word-display {
    margin-bottom: 30px;
}

.word {
    font-size: 36px;
    font-weight: bold;
    color: #333;
    margin-bottom: 10px;
}

.description {
    font-size: 18px;
    color: #666;
}

.user-input-area {
    display: flex;
    gap: 10px;
    justify-content: center;
    margin-bottom: 20px;
}

.answer-input {
    padding: 12px;
    font-size: 18px;
    border: 2px solid #ddd;
    border-radius: 4px;
    width: 300px;
}

.answer-input:focus {
    outline: none;
    border-color: #007bff;
}

.answer-input:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
}

.check-btn {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;
}

.check-btn:hover:not(:disabled) {
    background-color: #0056b3;
}

.check-btn:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
}

.feedback {
    margin-top: 20px;
}

.feedback-message {
    padding: 10px;
    border-radius: 4px;
    font-size: 18px;
    font-weight: bold;
}

.feedback-message.correct {
    background-color: #d4edda;
    color: #155724;
    border: 1px solid #c3e6cb;
}

.feedback-message.wrong {
    background-color: #f8d7da;
    color: #721c24;
    border: 1px solid #f5c6cb;
}

.results {
    background-color: #fff;
    border-radius: 8px;
    padding: 30px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.results h3 {
    margin-top: 0;
    color: #333;
    text-align: center;
}

.result-stats {
    display: flex;
    justify-content: space-around;
    margin-bottom: 30px;
}

.wrong-words {
    margin-top: 30px;
}

.wrong-words h4 {
    margin-top: 0;
    color: #333;
}

.wrong-words-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 15px;
}

.wrong-word-item {
    background-color: #f8f9fa;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 15px;
}

.wrong-word-item .word {
    font-weight: bold;
    color: #dc3545;
    display: block;
    margin-bottom: 5px;
}

.wrong-word-item .desc {
    font-size: 14px;
    color: #666;
    display: block;
    margin-bottom: 5px;
}

.wrong-word-item .user-answer {
    font-size: 14px;
    color: #888;
    display: block;
}

.result-actions {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-top: 30px;
}

.action-btn {
    background-color: #007bff;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
}

.action-btn:hover {
    background-color: #0056b3;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.loading-spinner {
    background-color: white;
    padding: 30px;
    border-radius: 8px;
    text-align: center;
}

.spinner {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #007bff;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 0 auto 15px;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

@media (max-width: 768px) {
    .practice-main {
        padding: 10px;
    }

    .control-panel {
        flex-direction: column;
        gap: 15px;
    }

    .practice-stats {
        flex-wrap: wrap;
        gap: 10px;
    }

    .stat-item {
        flex: 1 0 40%;
    }

    .result-stats {
        flex-wrap: wrap;
        gap: 10px;
    }

    .user-input-area {
        flex-direction: column;
        align-items: center;
    }

    .answer-input {
        width: 100%;
        max-width: 300px;
    }

    .wrong-words-list {
        grid-template-columns: 1fr;
    }
}
</style>